import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Typography,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Checkbox,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";

// Helper function to parse and format dates
const formatDate = (timestamp) => {
  // Check if timestamp is a Unix timestamp in milliseconds
  if (timestamp && !isNaN(timestamp) && timestamp.toString().length >= 10) {
    // If it's a string that can be parsed as a number
    if (typeof timestamp === "string") {
      // Try to parse it directly as a date first
      const dateObject = new Date(timestamp);
      if (!isNaN(dateObject.getTime())) {
        return dateObject.toLocaleString();
      }

      // Try to parse it as a Unix timestamp
      const unixTimestamp = parseInt(timestamp);
      const unixDate = new Date(unixTimestamp);
      if (!isNaN(unixDate.getTime())) {
        return unixDate.toLocaleString();
      }
    } else {
      // If it's already a number
      const date = new Date(parseInt(timestamp));
      if (!isNaN(date.getTime())) {
        return date.toLocaleString();
      }
    }
  }

  // Fallback to original value
  return timestamp || "N/A";
};

// Helper function to parse JSON strings
const parseJSONData = (jsonString) => {
  try {
    return typeof jsonString === "string" ? JSON.parse(jsonString) : jsonString;
  } catch (error) {
    console.error("Error parsing JSON:", error);
    return {};
  }
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead({
  columns,
  order,
  orderBy,
  onRequestSort,
  enableRowSelection,
  selectedCount,
  totalCount,
  onSelectAll,
}) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {enableRowSelection && (
          <TableCell padding="checkbox" sx={{ fontWeight: "bold", backgroundColor: (t) => t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100] }}>
            <Checkbox
              indeterminate={selectedCount > 0 && selectedCount < totalCount}
              checked={totalCount > 0 && selectedCount === totalCount}
              onChange={onSelectAll}
            />
          </TableCell>
        )}
        {columns.map((column) => (
          <TableCell
            key={column.field}
            align={column.align || "left"}
            padding={column.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === column.field ? order : false}
            sx={{
              fontWeight: "bold",
              whiteSpace: "nowrap",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.background.paper
                  : theme.palette.grey[100],
            }}
            style={{ minWidth: column.minWidth, width: column.width }}
          >
            <TableSortLabel
              active={orderBy === column.field}
              direction={orderBy === column.field ? order : "asc"}
              onClick={createSortHandler(column.field)}
            >
              {column.headerName || column.field}
            </TableSortLabel>
          </TableCell>
        ))}
        {/* <TableCell
          align="center"
          sx={{
            fontWeight: "bold",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.background.paper
                : theme.palette.grey[100],
          }}
        >
          Actions
        </TableCell> */}
      </TableRow>
    </TableHead>
  );
}

export default function DataTable({
  title,
  columns,
  data,
  loading,
  onEdit,
  onDelete,
  onRefresh,
  actionButtons,
  hideActions = false,
  isAdmin,
  exportFilename = "export",
  exportDataKeyPrefix,
  enableRowSelection = false,
  onSendEmailClick,
}) {
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("created");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showAllRows, setShowAllRows] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());

  // Process the data to handle formatting and JSON parsing
  const processedData = useMemo(() => {
    return data.map((row) => {
      const newRow = { ...row };

      // Handle "data" field if it exists and is JSON
      if (newRow.data && typeof newRow.data === "string") {
        try {
          newRow.parsedData = JSON.parse(newRow.data);
        } catch (e) {
          newRow.parsedData = {};
        }
      }

      // Format dates
      if (newRow.created) {
        newRow.formattedCreated = formatDate(newRow.created);
      }

      return newRow;
    });
  }, [data]);

  // Filter the data based on search term
  useEffect(() => {
    if (!processedData || processedData.length === 0) {
      setFilteredData([]);
      return;
    }

    if (!searchTerm.trim()) {
      setFilteredData(processedData);
      return;
    }

    const lowercasedFilter = searchTerm.toLowerCase();

    const filtered = processedData.filter((item) => {
      return Object.keys(item).some((key) => {
        const value = item[key];
        if (value == null) return false;

        // Check in parsedData for nested values
        if (key === "parsedData" && typeof value === "object") {
          return Object.values(value).some((nestedValue) =>
            String(nestedValue).toLowerCase().includes(lowercasedFilter)
          );
        }

        // Normal string comparison
        return String(value).toLowerCase().includes(lowercasedFilter);
      });
    });

    setFilteredData(filtered);
    setPage(0); // Reset to first page when filtering
  }, [processedData, searchTerm]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleEdit = () => {
    if (onEdit && selectedRow) {
      onEdit(selectedRow);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (onDelete && selectedRow) {
      onDelete(selectedRow);
    }
    handleMenuClose();
  };

  const exportToExcel = () => {
    // Prepare the data for export
    const exportData = filteredData.map((row) => {
      const exportRow = {};
      columns.forEach((column) => {
        if (column.field === "data" && row.parsedData) {
          // Flatten the nested JSON data; optional prefix for keys (e.g. "User ")
          const prefix = exportDataKeyPrefix ?? "";
          Object.entries(row.parsedData).forEach(([key, value]) => {
            exportRow[prefix + key] = value;
          });
        } else if (column.field === "created") {
          exportRow[column.headerName || column.field] =
            row.formattedCreated || row[column.field];
        } else {
          exportRow[column.headerName || column.field] = row[column.field];
        }
      });
      return exportRow;
    });

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

    // Generate filename with date
    const date = new Date().toISOString().slice(0, 10);
    const filename = `${exportFilename}_${date}.xlsx`;

    // Export
    XLSX.writeFile(workbook, filename);
  };

  // Render cell content with formatting
  const renderCellContent = (row, column) => {
    const value = row[column.field];

    if (column.field === "created") {
      return row.formattedCreated || formatDate(value);
    }

    if (column.field === "data" && row.parsedData) {
      // For the data field, display key information from the parsed JSON
      return (
        <Box>
          {Object.entries(row.parsedData)
            .slice(0, 3)
            .map(([key, val]) => (
              <Typography key={key} variant="body2" noWrap>
                <strong>{key}:</strong> {val}
              </Typography>
            ))}
          {Object.keys(row.parsedData).length > 3 && (
            <Tooltip title={JSON.stringify(row.parsedData, null, 2)}>
              <Typography variant="body2" color="text.secondary">
                ...and {Object.keys(row.parsedData).length - 3} more
              </Typography>
            </Tooltip>
          )}
        </Box>
      );
    }

    if (column.renderCell) {
      return column.renderCell({ row });
    }

    // Default rendering
    return value;
  };

  // Get visible rows
  const visibleRows = useMemo(() => {
    const sorted = stableSort(filteredData, getComparator(order, orderBy));

    if (showAllRows) {
      return sorted;
    }

    return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredData, order, orderBy, page, rowsPerPage, showAllRows]);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, overflow: "hidden" }} elevation={3}>
        <Box sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            {title && (
              <Typography variant="h6" component="div">
                {title}
              </Typography>
            )}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <TextField
                variant="outlined"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearSearch}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                size="small"
                sx={{ minWidth: { sm: 200 } }}
              />

              {enableRowSelection && selectedRowIds.size > 0 && onSendEmailClick && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<EmailIcon />}
                  size="small"
                  onClick={() => {
                    const selectedRows = filteredData.filter((r) =>
                      selectedRowIds.has(String(r.id)),
                    );
                    onSendEmailClick(selectedRows);
                  }}
                >
                  Send Email ({selectedRowIds.size})
                </Button>
              )}

              {isAdmin && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  size="small"
                  onClick={exportToExcel}
                  disabled={filteredData.length === 0}
                >
                  Export
                </Button>
              )}

              {onRefresh && (
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  size="small"
                  onClick={onRefresh}
                >
                  Refresh
                </Button>
              )}

              {actionButtons}
            </Stack>
          </Stack>

          <FormControl size="small" sx={{ mb: 2, minWidth: 150 }}>
            <InputLabel id="rows-per-page-label">Display Mode</InputLabel>
            <Select
              labelId="rows-per-page-label"
              value={showAllRows ? "all" : rowsPerPage}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "all") {
                  setShowAllRows(true);
                } else {
                  setShowAllRows(false);
                  setRowsPerPage(Number(value));
                }
              }}
              label="Display Mode"
            >
              <MenuItem value={10}>10 per page</MenuItem>
              <MenuItem value={25}>25 per page</MenuItem>
              <MenuItem value={50}>50 per page</MenuItem>
              <MenuItem value={100}>100 per page</MenuItem>
              <MenuItem value="all">Show all ({filteredData.length})</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader aria-label="sticky table" size="medium">
            <EnhancedTableHead
              columns={columns}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              enableRowSelection={enableRowSelection}
              selectedCount={selectedRowIds.size}
              totalCount={visibleRows.length}
              onSelectAll={(e) => {
                if (e.target.checked) {
                  setSelectedRowIds(
                    new Set(visibleRows.map((r) => String(r.id))),
                  );
                } else {
                  setSelectedRowIds(new Set());
                }
              }}
            />
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading data...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <Typography variant="body1">
                      {searchTerm ? "No results found." : "No data available."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row, index) => (
                  <TableRow
                    hover
                    key={`${row.id}-${index}` || index}
                    selected={enableRowSelection && selectedRowIds.has(String(row.id))}
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.action.hover
                            : theme.palette.grey[50],
                      },
                    }}
                  >
                    {enableRowSelection && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRowIds.has(String(row.id))}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSelectedRowIds((prev) => {
                              const next = new Set(prev);
                              const id = String(row.id);
                              if (next.has(id)) next.delete(id);
                              else next.add(id);
                              return next;
                            });
                          }}
                        />
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell
                        key={`${row.id || index}-${column.field}`}
                        align={column.align || "left"}
                      >
                        {renderCellContent(row, column)}
                      </TableCell>
                    ))}

                    {/* {!hideActions && (
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(event) => handleMenuOpen(event, row)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    )} */}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!showAllRows && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {onEdit && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
